import { useEffect, useRef } from 'react';
import debounce from '@mui/material/utils/debounce';
import { RealSize } from '../../../types';
import { getMaxSize } from '../../../utils/ratio';
import { useEditorData } from '../providers/EditorProvider';
import handleAddWebSite from '../utils/handleWebSite';
import handleWorldClockAndDate, {
  getDateAndTimeSource,
} from '../utils/handleWorldClockAndDate';
import { getDimensions, handleRatio } from '../utils/common';
import { IFRAME_WIDGETS } from '../../../config';
import { useHandleHistory } from './useHandleHistory';

export const CANVAS_MARGIN = 32;
const RESIZE_DEBOUNCE_TIME = 100;

const { WORLD_CLOCK, DATE, IFRAME } = IFRAME_WIDGETS;

const getSize = (container: HTMLElement, realSize: RealSize) => {
  const canvasWidth = Math.max(container.clientWidth - CANVAS_MARGIN * 2, 1);
  const canvasHeight = Math.max(container.clientHeight - CANVAS_MARGIN * 2, 1);

  return getMaxSize({
    canvasWidth,
    canvasHeight,
    width: realSize.realWidth,
    height: realSize.realHeight,
  });
};

export const useHandleResize = (
  canvasParentRef: React.RefObject<HTMLDivElement>,
  realSize: { realWidth: number; realHeight: number },
  openPreview?: boolean,
) => {
  const { canvas, zoomScale, setAppliedZoom, zoomAnchorRef } = useEditorData();
  const { handleHistory } = useHandleHistory();
  const appliedSize = useRef<{
    canvas: fabric.Canvas;
    width: number;
    height: number;
  } | null>(null);

  // Widget overlays only need moving/resizing; recreating them reloads every
  // iframe. Clock/date content is laid out in pixels, so it is regenerated.
  const repositionWidgets = (canvas: fabric.Canvas) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    for (const obj of canvas.getObjects()) {
      const type = obj?.data?.type;
      if (!Object.values(IFRAME_WIDGETS).includes(type)) continue;
      const dimensions = getDimensions(canvas, obj);
      const elements = Array.from(
        document.getElementsByClassName(obj.data.className),
      ) as HTMLElement[];
      for (const element of elements) {
        handleRatio(dimensions, element);
        if (
          element instanceof HTMLIFrameElement &&
          (type === WORLD_CLOCK || type === DATE)
        ) {
          element.srcdoc = getDateAndTimeSource(
            timezone,
            dimensions.width,
            dimensions.height,
            obj.data.color || '#000000',
            type === WORLD_CLOCK,
            true,
          );
        }
      }
    }
  };

  // `recreateWidgets` is needed after the preview closes: its clock/date
  // widgets reuse the editor's classNames and remove the editor's elements.
  const handleResize = (recreateWidgets = false) => {
    const container = canvasParentRef.current;
    if (canvas && container) {
      const fitted = getSize(container, realSize);
      const scale = zoomScale ?? fitted.width / realSize.realWidth;
      const width = realSize.realWidth * scale;
      const height = realSize.realHeight * scale;
      const last = appliedSize.current;
      const sizeChanged =
        last?.canvas !== canvas ||
        last.width !== width ||
        last.height !== height;
      if (!sizeChanged && !recreateWidgets) return;
      appliedSize.current = { canvas, width, height };
      if (sizeChanged) {
        // Keep the slide point under the anchor (the pointer, or the middle
        // of the workspace) where it is on screen.
        const containerRect = container.getBoundingClientRect();
        const anchor = zoomAnchorRef.current ?? {
          clientX: containerRect.left + containerRect.width / 2,
          clientY: containerRect.top + containerRect.height / 2,
        };
        zoomAnchorRef.current = null;
        const before = canvas.getElement().getBoundingClientRect();
        const previousScale = canvas.getZoom() || scale;
        const slideX = (anchor.clientX - before.left) / previousScale;
        const slideY = (anchor.clientY - before.top) / previousScale;

        canvas.setZoom(scale);
        canvas.setHeight(height);
        canvas.setWidth(width);
        canvas.requestRenderAll();
        setAppliedZoom(scale);

        if (zoomScale !== null) {
          const after = canvas.getElement().getBoundingClientRect();
          container.scrollLeft += after.left + slideX * scale - anchor.clientX;
          container.scrollTop += after.top + slideY * scale - anchor.clientY;
        }
      }
      if (!recreateWidgets) {
        repositionWidgets(canvas);
        return;
      }
      for (const obj of canvas.getObjects()) {
        if (Object.values(IFRAME_WIDGETS).includes(obj?.data?.type)) {
          document.getElementsByClassName(obj?.data?.className)?.[0]?.remove();
        }
        switch (obj?.data?.type) {
          case IFRAME:
            handleAddWebSite(canvas, obj?.data.src, handleHistory, obj);
            break;
          case WORLD_CLOCK:
            handleWorldClockAndDate({ canvas, obj, handleHistory });
            break;
          case DATE:
            handleWorldClockAndDate({
              canvas,
              obj,
              isTime: false,
              handleHistory,
            });
            break;
        }
      }
    }
  };

  useEffect(() => {
    if (!openPreview) handleResize(true);
  }, [openPreview]);

  useEffect(() => {
    try {
      handleResize();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Ignore error', error);
    }
    // Panels opening/closing resize the container without a window resize.
    const container = canvasParentRef.current;
    if (!container) return;
    const onResize = debounce(() => handleResize(), RESIZE_DEBOUNCE_TIME);
    const observer = new ResizeObserver(onResize);
    observer.observe(container);
    return () => {
      observer.disconnect();
      onResize.clear();
    };
  }, [canvas, zoomScale]);
};
